import { ActionType, StateContext } from '@ngxs/store';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentsAccountsStateModel } from '../../features/payments/store/state/accounts/accounts.state';
import { TemplatesStateModel } from '../../features/payments/store/state/templates/templates.state';
import { PaymentsStateModel } from '../../features/payments/store/state/payments/payments.state';
import { WorkflowsStateModel } from '../../features/payments/store/state/workflows/workflow.state';

import { InstructionsStateModel } from '@trovata/app/features/payments/store/state/instructions/instructions.state';

export interface StateApiError {
	stateActionType: string;
	endpoint: string;
	errorMessage: string;
	statusCode: number;
}

export type TrovataAppPaymentsStateModel = PaymentsStateModel | PaymentsAccountsStateModel | TemplatesStateModel | WorkflowsStateModel | InstructionsStateModel;

export function checkToRemoveStateApiError<T extends TrovataAppPaymentsStateModel>(context: StateContext<T>, stateAction: ActionType): void {
	const state: T = context.getState();
	if (state && state.apiErrors) {
		state.apiErrors = state.apiErrors.filter((stateApiError: StateApiError) => stateApiError.stateActionType !== stateAction.type);
		context.patchState(state);
	}
}

export function checkToAddStateApiError<T extends TrovataAppPaymentsStateModel>(
	error: HttpErrorResponse | Error,
	context: StateContext<T>,
	stateAction: ActionType,
	customErrorMessage: string
): HttpErrorResponse | Error {
	if (error instanceof HttpErrorResponse) {
		const errorMessage: string = getErrorMessage(error, customErrorMessage);
		const stateApiError: StateApiError = {
			stateActionType: stateAction.type,
			endpoint: error.url,
			errorMessage: errorMessage,
			statusCode: error.status,
		};
		const foundStateApiError: boolean = findStateApiError(context, stateApiError);
		if (!foundStateApiError) {
			const state: T = context.getState();
			state.apiErrors.push(stateApiError);
			state.apiErrors = JSON.parse(JSON.stringify(state.apiErrors));
			context.patchState(state);
		}
	}
	return error;
}

function getErrorMessage(httpError: HttpErrorResponse, customErrorMessage: string): string {
	// TODO: Remove after api errors have been standardized
	let errorMessage: string;
	if (httpError.status === 500 || httpError.status === 0 || isHttpErrorMessageBad(httpError)) {
		errorMessage = customErrorMessage;
	} else if (httpError.error && typeof httpError.error === 'string') {
		errorMessage = httpError.error;
	} else if (httpError.error && httpError.error.error && typeof httpError.error.error === 'string') {
		errorMessage = httpError.error.error;
	} else if (httpError.error && httpError.error.message && typeof httpError.error.message === 'string') {
		errorMessage = httpError.error.message;
	} else {
		errorMessage = customErrorMessage;
	}
	return errorMessage;
}

function findStateApiError<T extends TrovataAppPaymentsStateModel>(context: StateContext<T>, stateApiError: StateApiError): boolean {
	const state: T = context.getState();
	if (!state || !state.apiErrors) {
		return false;
	}
	const stateApiErrorString: string = JSON.stringify(stateApiError);
	let foundApiErrorString: boolean = false;
	state.apiErrors.forEach((apiError: StateApiError) => {
		const apiErrorString: string = JSON.stringify(apiError);
		if (stateApiErrorString === apiErrorString) {
			foundApiErrorString = true;
		}
	});
	return foundApiErrorString;
}

function isHttpErrorMessageBad(httpError: HttpErrorResponse): boolean {
	// TODO: Remove after api errors have been standardized
	let httpErrorMessageIsBad: boolean = false;
	if (
		(httpError.error && typeof httpError.error === 'string' && httpError.error.toLowerCase() === 'unknown error') ||
		(httpError.error && httpError.error.error && typeof httpError.error.error === 'string' && httpError.error.error.toLowerCase() === 'unknown error') ||
		(httpError.error && httpError.error.message && typeof httpError.error.message === 'string' && httpError.error.message.toLowerCase() === 'unknown error')
	) {
		httpErrorMessageIsBad = true;
	}
	return httpErrorMessageIsBad;
}

export function checkForBadHttpErrorMessage(httpError: HttpErrorResponse, customErrorMessage: string): HttpErrorResponse {
	// TODO: Remove after api errors have been standardized
	if (httpError.status === 500 || httpError.status === 0 || isHttpErrorMessageBad(httpError)) {
		(httpError as Error).message = customErrorMessage;
	} else {
		(httpError as Error).message = getErrorMessage(httpError, customErrorMessage);
	}
	return httpError;
}

export function openStateApiErrorSnackBar(apiError: StateApiError, startTimeouts: boolean, snackBar: MatSnackBar): void {
	const snackBarTimeOutTime: number = 3000;
	if (!startTimeouts) {
		snackBar.open(apiError.errorMessage, null, {
			duration: snackBarTimeOutTime,
		});
	} else {
		setTimeout(() => {
			snackBar.open(apiError.errorMessage, null, {
				duration: snackBarTimeOutTime,
			});
		}, snackBarTimeOutTime);
	}
}

export function showAnyStateApiErrors(apiErrors: StateApiError[], snackBar: MatSnackBar): void {
	if (apiErrors.length) {
		let startTimeouts: boolean;
		apiErrors.forEach((apiError: StateApiError, index: number) => {
			if (index === 0) {
				startTimeouts = false;
			} else {
				startTimeouts = true;
			}
			openStateApiErrorSnackBar(apiError, startTimeouts, snackBar);
		});
	}
}
